import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Users, MessageCircle, BarChart3, BrainCircuit, Zap, Radio, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivities } from '../context/ActivitiesContext';
import { cn, getRoomPath } from '../utils';
import { motion } from 'framer-motion';
import RoomCard from '../components/RoomCard';
import { RoomCardSkeleton, Skeleton } from '../components/Skeleton';

import { canManagePlatform } from '../utils/permissions';

const FeatureCard = ({ icon: Icon, title, description, accent }) => (
  <div className="bg-white border border-gray-100 rounded-3xl p-8 hover:shadow-2xl hover:shadow-brand-indigo/10 transition-all duration-300 flex flex-col items-center md:items-start text-center md:text-left shadow-sm group">
    <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg transition-transform group-hover:scale-110", accent)}>
      <Icon size={24} className="text-white" />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-4">{title}</h3>
    <p className="text-sm text-gray-500 font-medium leading-relaxed">{description}</p>
  </div>
);

const UseCaseCard = ({ title, description }) => (
  <div className="bg-white border border-gray-100 rounded-3xl p-8 hover:shadow-2xl hover:shadow-brand-indigo/10 transition-all duration-300 flex flex-col items-center md:items-start text-center md:text-left shadow-sm group">
    <h4 className="text-lg font-bold text-gray-900 mb-4 group-hover:text-brand-indigo transition-colors">{title}</h4>
    <p className="text-sm text-gray-500 font-medium leading-relaxed">{description}</p>
  </div>
);

const Home = () => {
  const { user } = useAuth();
  const {
    happeningNow,
    liveHighlights,
    trendingHubs,
    loading
  } = useActivities();
  const navigate = useNavigate();

  useEffect(() => {
    if (canManagePlatform(user)) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const useCases = [
    { title: 'Universities', description: 'Classrooms, labs, student clubs, and campus-driven collaboration hubs.' },
    { title: 'Workshops', description: 'Facilitated idea sessions, innovation sprints, and active breakout experiences.' },
    { title: 'Conferences', description: 'Live audience Q&A, event polls, and real-time community follow-up.' },
    { title: 'Webinars', description: 'Interactive learning with persistent question threads and engagement analytics.' },
    { title: 'Hackathons', description: 'Fast ideation, team voting, and decision-making during sprint cycles.' },
    { title: 'Communities', description: 'Persistent groups for ongoing knowledge, announcements, and live rooms.' },
  ];

  const pillars = [
    { icon: MessageCircle, title: 'Live Q&A', description: 'Audience-driven questions, upvoting, speaker interaction, and moderator control.', accent: 'bg-purple-500' },
    { icon: BrainCircuit, title: 'Ideation Hub', description: 'Shared idea boards, prioritization, collaborative workshops, and voting.', accent: 'bg-green-600' },
    { icon: BarChart3, title: 'Polls & Decisions', description: 'Live voting, word clouds, quizzes, timed feedback, and sentiment checks.', accent: 'bg-brand-indigo' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 md:pt-10 pb-0">
      <section className="mb-16 text-center md:text-left">
        {user && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-brand-indigo/5 text-brand-indigo px-4 py-2 rounded-2xl font-bold text-[10px] uppercase tracking-[0.15em] mb-6"
          >
            <Zap size={14} />
            Welcome back, {user.name}!
          </motion.div>
        )}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-gray-900 leading-[1.1] mb-8 tracking-tighter"
        >
          Communities <br />
          <span className="text-brand-indigo">Interacting Live.</span>
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-lg md:text-xl text-gray-500 mb-10 max-w-3xl md:mx-0 mx-auto leading-relaxed font-medium"
        >
          Host live Q&A sessions, brainstorm ideas, run interactive workshops, and build communities that continue beyond the event.
        </motion.p>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start"
        >
          {user ? (
            <Link to="/rm/lv/new" className="w-full sm:w-auto bg-brand-indigo hover:bg-brand-indigo/90 text-white font-bold py-4 px-8 rounded-2xl shadow-xl shadow-brand-indigo/20 transition-all hover:scale-[1.02] active:scale-[0.98] text-center text-sm">
              Launch a Session
            </Link>
          ) : (
            <Link to="/signup" state={{ from: { pathname: '/rm/lv/new' } }} className="w-full sm:w-auto bg-brand-indigo hover:bg-brand-indigo/90 text-white font-bold py-4 px-8 rounded-2xl shadow-xl shadow-brand-indigo/20 transition-all hover:scale-[1.02] active:scale-[0.98] text-center text-sm">
              Launch a Session
            </Link>
          )}
          <Link to="/live" className="w-full sm:w-auto bg-transparent hover:bg-gray-50 text-gray-900 font-bold py-4 px-8 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] text-center text-sm">
            Join the Ecosystem
          </Link>
        </motion.div>
      </section>

      <section className="mb-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Happening Now</h2>
            <p className="text-gray-500 font-medium text-sm mt-1">Realtime activity across the ecosystem</p>
          </div>
          <div className="inline-flex items-center justify-center md:justify-start gap-2 text-[10px] font-bold text-brand-cyan uppercase tracking-[0.15em]">
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-cyan"></span>
            </span>
            {Array.isArray(happeningNow) && happeningNow.length > 0 
              ? `${happeningNow.reduce((acc, r) => acc + (r.participants || 0), 0).toLocaleString()} active now` 
              : '0 active now'}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            Array(3).fill(0).map((_, i) => <RoomCardSkeleton key={i} />)
          ) : Array.isArray(happeningNow) && happeningNow.length > 0 ? (
            happeningNow.map((room) => (
              <RoomCard 
                key={room.id}
                room={room}
                featured
              />
            ))
          ) : (
            <div className="col-span-full py-12 bg-gray-50/50 rounded-[2.5rem] border border-dashed border-gray-200 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-gray-300 mb-4">
                <Radio size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 tracking-tight">Quiet for now</h3>
              <p className="text-sm text-gray-500 font-medium mt-1">Check back later or launch your own session.</p>
              <Link to="/rm/lv/new" className="mt-6 text-brand-indigo font-bold text-[10px] uppercase tracking-[0.15em] flex items-center gap-2 hover:gap-3 transition-all">
                Host a Q&A <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
        {Array.isArray(happeningNow) && happeningNow.length > 0 && (
          <div className="mt-8 flex justify-center">
            <Link to="/live" className="text-[10px] font-bold text-gray-400 hover:text-brand-indigo transition-colors uppercase tracking-[0.15em] flex items-center gap-2">
              Explore all live rooms <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </section>

      {/* Interaction Snippets */}
      <section className="mb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-indigo mb-3">Live highlights</p>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Active participation in the ecosystem.</h2>
          </div>
          <p className="text-gray-500 max-w-xl md:mx-0 mx-auto font-medium text-sm leading-relaxed">From trending questions to collaborative ideas, see how communities are interacting right now.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm flex flex-col h-full">
                <Skeleton className="h-4 w-24 mb-4 rounded-lg" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-2/3 mb-6" />
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-4 w-8" />
                </div>
              </div>
            ))
          ) : Array.isArray(liveHighlights) && liveHighlights.length > 0 ? (
            liveHighlights.map((disc) => (
              <div key={disc.id} className="bg-white border border-gray-100 rounded-3xl p-6 hover:shadow-2xl hover:shadow-brand-indigo/5 transition-all shadow-sm group flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold text-brand-indigo bg-brand-indigo/10 px-2 py-0.5 rounded-lg uppercase tracking-[0.15em]">Trending Topic</span>
                  {(disc.privacy === 'private' || disc.has_password) && <Lock size={12} className="text-gray-300" />}
                </div>
                <p className="text-lg font-bold text-gray-900 mb-6 leading-tight group-hover:text-brand-indigo transition-colors tracking-tight">"{disc.title}"</p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full border border-gray-100 overflow-hidden bg-gray-50">
                      <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${disc.author}`} alt="avatar" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-500 truncate max-w-[120px] uppercase tracking-[0.15em]">{disc.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-brand-indigo font-bold text-sm">
                    <Zap size={14} className="fill-brand-indigo" />
                    {disc.likes || 0}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-12 bg-gray-50/50 rounded-[2.5rem] border border-dashed border-gray-200 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-gray-300 mb-4">
                <MessageCircle size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 tracking-tight">No highlights yet</h3>
              <p className="text-sm text-gray-500 font-medium mt-1">Start a discussion in your community to see it here.</p>
            </div>
          )}
        </div>
      </section>

      <section className="mb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-indigo mb-3">What Chooziq is for</p>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Designed for events, workshops, communities, and learning.</h2>
          </div>
          <p className="text-gray-500 max-w-xl md:mx-0 mx-auto font-medium text-sm leading-relaxed">Use Chooziq for classrooms, hackathons, conferences, webinars, and long-term community hubs that keep engagement alive.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((item) => (
            <UseCaseCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="mb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-indigo mb-3">Core features</p>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">The interaction pillars that power every experience.</h2>
          </div>
          <p className="text-gray-500 max-w-xl md:mx-0 mx-auto font-medium text-sm leading-relaxed">Live Q&A, Brainstorming, and Polls form the reusable interaction primitives that make Chooziq feel alive and collaborative.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
